import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './QsFeed.css';

const API = process.env.REACT_APP_API_URL;
const MAX_CHARS = 280;

const timeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return `${diff}s`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return new Date(dateStr).toLocaleDateString('es-EC', { day: 'numeric', month: 'short' });
};

const Avatar = ({ username, photoUrl, size = 40 }) => {
  const initial = (username || '?')[0].toUpperCase();
  return photoUrl
    ? <img src={photoUrl} alt={username} className="qs-avatar-img" style={{ width: size, height: size }} />
    : <div className="qs-avatar-placeholder" style={{ width: size, height: size }}>{initial}</div>;
};

// ── Comments section ──────────────────────────────────────────────
const CommentsSection = ({ post, currentUser, isLoggedIn }) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [text, setText]         = useState('');
  const [posting, setPosting]   = useState(false);

  const token = () => localStorage.getItem('access_token');

  useEffect(() => {
    axios.get(`${API}/qs/${post.id}/comments/`)
      .then(r => setComments(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [post.id]);

  const handleComment = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const r = await axios.post(
        `${API}/qs/${post.id}/comments/`,
        { text: text.trim() },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setComments(prev => [...prev, r.data]);
      setText('');
    } catch { /* ignore */ } finally { setPosting(false); }
  };

  const handleDeleteComment = async (id) => {
    try {
      await axios.delete(`${API}/qs/comments/${id}/`, { headers: { Authorization: `Bearer ${token()}` } });
      setComments(prev => prev.filter(c => c.id !== id));
    } catch {}
  };

  const handleCommentLike = async (commentId) => {
    try {
      const r = await axios.post(`${API}/qs/comments/${commentId}/like/`, {}, { headers: { Authorization: `Bearer ${token()}` } });
      setComments(prev => prev.map(c =>
        c.id === commentId ? { ...c, liked_by_me: r.data.liked, likes_count: r.data.likes_count } : c
      ));
    } catch {}
  };

  return (
    <div className="qs-comments-section">
      {loading && <div className="qs-comments-loading">{t('loading_comments')}</div>}
      {comments.map(c => (
        <div key={c.id} className="qs-comment">
          <Avatar username={c.username} photoUrl={c.photo_url} size={28} />
          <div className="qs-comment-body">
            <span className="qs-comment-username">@{c.username}</span>
            <span className="qs-comment-time">{timeAgo(c.created_at)}</span>
            <p className="qs-comment-text">{renderPostText(c.text)}</p>
            <div className="qs-comment-actions">
              {isLoggedIn && (
                <button className={`qs-like-btn qs-like-btn--sm ${c.liked_by_me ? 'qs-like-btn--active' : ''}`} onClick={() => handleCommentLike(c.id)}>
                  ♥ {c.likes_count > 0 ? c.likes_count : ''}
                </button>
              )}
              {isLoggedIn && currentUser?.username === c.username && (
                <button className="qs-delete-btn" onClick={() => handleDeleteComment(c.id)} title="Eliminar">✕</button>
              )}
            </div>
          </div>
        </div>
      ))}
      {isLoggedIn ? (
        <div className="qs-comment-composer">
          <Avatar username={currentUser?.username} photoUrl={currentUser?.photo_url} size={28} />
          <div className="qs-comment-input-wrap">
            <input
              className="qs-comment-input"
              type="text"
              placeholder={t('write_comment')}
              value={text}
              onChange={e => setText(e.target.value.slice(0, 280))}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(); } }}
              maxLength={280}
            />
            <button className="qs-comment-send-btn" onClick={handleComment} disabled={posting || !text.trim()}>
              {posting ? '…' : '→'}
            </button>
          </div>
        </div>
      ) : (
        <p className="qs-comments-gate">{t('login_to_comment')}</p>
      )}
    </div>
  );
};

// ── Render texto con @menciones resaltadas ────────────────────────
const renderPostText = (text) => {
  const parts = text.split(/(@\w+)/g);
  return parts.map((part, i) =>
    /^@\w+$/.test(part)
      ? <span key={i} className="qs-mention">{part}</span>
      : part
  );
};

// ── Main QsFeed ───────────────────────────────────────────────────
const QsFeed = ({ isLoggedIn, currentUser, onViewProfile }) => {
  const { t } = useTranslation();
  const [posts, setPosts]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [text, setText]             = useState('');
  const [posting, setPosting]       = useState(false);
  const [error, setError]           = useState(null);
  const [postError, setPostError]   = useState(null);
  const [expanded, setExpanded]     = useState(new Set());

  // Salas
  const [rooms, setRooms]               = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null); // null = público
  const [roomMembers, setRoomMembers]   = useState([]);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [createName, setCreateName]     = useState('');
  const [createDesc, setCreateDesc]     = useState('');
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [showRoomPanel, setShowRoomPanel] = useState(false);
  const [inviteUsername, setInviteUsername] = useState('');
  const [inviteMsg, setInviteMsg]       = useState('');

  const token = () => localStorage.getItem('access_token');
  const authHeader = () => ({ Authorization: `Bearer ${token()}` });

  // ── Fetch pública ──────────────────────────────────────────────
  const fetchPublicPosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/qs/`, isLoggedIn ? { headers: authHeader() } : {});
      setPosts(r.data);
    } catch {
      setError(t('feed_load_error'));
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  // ── Fetch sala ─────────────────────────────────────────────────
  const fetchRoomPosts = useCallback(async (room) => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/rooms/${room.id}/qs/`, { headers: authHeader() });
      setPosts(r.data);
    } catch {
      setError(t('room_feed_load_error'));
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Fetch salas ────────────────────────────────────────────────
  const fetchRooms = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const r = await axios.get(`${API}/rooms/`, { headers: authHeader() });
      setRooms(r.data);
    } catch {}
  }, [isLoggedIn]);

  // ── Fetch miembros de sala ─────────────────────────────────────
  const fetchRoomMembers = useCallback(async (room) => {
    try {
      const r = await axios.get(`${API}/rooms/${room.id}/members/`, { headers: authHeader() });
      setRoomMembers(r.data);
    } catch {}
  }, []);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  useEffect(() => {
    setPosts([]);
    setExpanded(new Set());
    setShowRoomPanel(false);
    if (selectedRoom) {
      fetchRoomPosts(selectedRoom);
      fetchRoomMembers(selectedRoom);
    } else {
      fetchPublicPosts();
    }
  }, [selectedRoom, fetchPublicPosts, fetchRoomPosts, fetchRoomMembers]);

  // ── Seleccionar sala ───────────────────────────────────────────
  const selectRoom = (room) => {
    setSelectedRoom(room);
    setInviteMsg('');
    setInviteUsername('');
  };

  // ── Audio recorder state ───────────────────────────────────────
  const [recording, setRecording]       = useState(false);
  const [audioBlob, setAudioBlob]       = useState(null);
  const [audioUrl, setAudioUrl]         = useState('');      // URL local para preview
  const [audioDuration, setAudioDuration] = useState(0);    // segundos grabados
  const [recSeconds, setRecSeconds]     = useState(0);       // contador en vivo
  const mediaRecorderRef = useRef(null);
  const audioChunksRef   = useRef([]);
  const recTimerRef      = useRef(null);
  const MAX_REC = 45;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      audioChunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setRecSeconds(0);
      recTimerRef.current = setInterval(() => {
        setRecSeconds(s => {
          if (s + 1 >= MAX_REC) { stopRecording(); return MAX_REC; }
          return s + 1;
        });
      }, 1000);
    } catch {
      alert('No se pudo acceder al micrófono.');
    }
  };

  const stopRecording = () => {
    clearInterval(recTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      setAudioDuration(recSeconds);
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const discardAudio = () => {
    setAudioBlob(null);
    setAudioUrl('');
    setAudioDuration(0);
    setRecSeconds(0);
  };

  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // ── Publicar Q ─────────────────────────────────────────────────
  const handlePost = async () => {
    if (!text.trim() && !audioBlob) return;
    setPosting(true);
    setPostError(null);
    try {
      let uploadedAudioUrl = '';
      let uploadedDuration = audioDuration;

      if (audioBlob) {
        const fd = new FormData();
        fd.append('audio', audioBlob, 'voice.webm');
        const up = await axios.post(`${API}/qs/upload-audio/`, fd, {
          headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
        });
        uploadedAudioUrl = up.data.audio_url;
      }

      const payload = { text: text.trim() };
      if (uploadedAudioUrl) { payload.audio_url = uploadedAudioUrl; payload.audio_duration = uploadedDuration; }
      if (selectedRoom) payload.room = selectedRoom.id;

      const r = await axios.post(`${API}/qs/`, payload, { headers: authHeader() });
      setPosts(prev => [{ ...r.data, likes_count: 0, comments_count: 0, liked_by_me: false, reposts_count: 0, reposted_by_me: false }, ...prev]);
      setText('');
      discardAudio();
    } catch (err) {
      setPostError(err.response?.data?.error || 'Error al publicar.');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/qs/${id}/`, { headers: authHeader() });
      setPosts(prev => prev.filter(p => p.id !== id));
    } catch {}
  };

  const [mutedUsers, setMutedUsers] = useState(new Set());
  const [reporting, setReporting]   = useState(null); // post being reported
  const [reportReason, setReportReason] = useState('');
  const [reportDetail, setReportDetail] = useState('');
  const [reportSent, setReportSent] = useState(false);

  const handleReportSubmit = async () => {
    if (!reportReason || !reporting) return;
    try {
      await axios.post(
        `${API}/qs/${reporting.id}/report/`,
        { reason: reportReason, detail: reportDetail.trim() },
        { headers: authHeader() }
      );
      setReportSent(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al enviar la denuncia.');
    }
  };

  const closeReport = () => {
    setReporting(null);
    setReportReason('');
    setReportDetail('');
    setReportSent(false);
  };

  const handleMute = async (username) => {
    try {
      await axios.post(`${API}/users/${username}/mute/`, {}, { headers: authHeader() });
      setMutedUsers(prev => new Set([...prev, username]));
      // Ocultar sus posts del feed localmente de inmediato
      setPosts(prev => prev.filter(p => {
        const postUser = p.original_post ? p.original_post.username : p.username;
        return postUser !== username;
      }));
    } catch {}
  };

  const [quoting, setQuoting]   = useState(null);  // post being quoted
  const [quoteText, setQuoteText] = useState('');

  const handleRepost = async (post) => {
    if (!isLoggedIn) return;
    // Optimistic toggle
    setPosts(prev => prev.map(p =>
      p.id === post.id
        ? { ...p, reposted_by_me: !p.reposted_by_me, reposts_count: p.reposted_by_me ? p.reposts_count - 1 : p.reposts_count + 1 }
        : p
    ));
    try {
      const r = await axios.post(`${API}/qs/${post.id}/repost/`, {}, { headers: authHeader() });
      if (r.data.reposted) {
        setPosts(prev => [r.data.post, ...prev.map(p =>
          p.id === post.id ? { ...p, reposted_by_me: true, reposts_count: r.data.reposts_count } : p
        )]);
      } else {
        setPosts(prev => prev
          .filter(p => !(p.original_post?.id === post.id && p.text === ''))
          .map(p => p.id === post.id ? { ...p, reposted_by_me: false, reposts_count: r.data.reposts_count } : p)
        );
      }
    } catch {
      setPosts(prev => prev.map(p =>
        p.id === post.id ? { ...p, reposted_by_me: post.reposted_by_me, reposts_count: post.reposts_count } : p
      ));
    }
  };

  const handleQuoteSubmit = async () => {
    if (!quoteText.trim() || !quoting) return;
    try {
      const r = await axios.post(`${API}/qs/${quoting.id}/repost/`, { text: quoteText.trim() }, { headers: authHeader() });
      setPosts(prev => [r.data.post, ...prev.map(p =>
        p.id === quoting.id ? { ...p, reposts_count: r.data.reposts_count } : p
      )]);
      setQuoting(null);
      setQuoteText('');
    } catch (err) {
      alert(err.response?.data?.error || 'Error al citar.');
    }
  };

  const handleLike = async (post) => {
    if (!isLoggedIn) return;
    setPosts(prev => prev.map(p =>
      p.id === post.id ? { ...p, liked_by_me: !p.liked_by_me, likes_count: p.liked_by_me ? p.likes_count - 1 : p.likes_count + 1 } : p
    ));
    try {
      const r = await axios.post(`${API}/qs/${post.id}/like/`, {}, { headers: authHeader() });
      setPosts(prev => prev.map(p =>
        p.id === post.id ? { ...p, liked_by_me: r.data.liked, likes_count: r.data.likes_count } : p
      ));
    } catch {
      setPosts(prev => prev.map(p =>
        p.id === post.id ? { ...p, liked_by_me: post.liked_by_me, likes_count: post.likes_count } : p
      ));
    }
  };

  const toggleComments = (postId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(postId) ? next.delete(postId) : next.add(postId);
      return next;
    });
  };

  // ── Crear sala ─────────────────────────────────────────────────
  const handleCreateRoom = async (e) => {
    e.preventDefault();
    if (!createName.trim()) return;
    setCreatingRoom(true);
    try {
      const r = await axios.post(`${API}/rooms/`, { name: createName.trim(), description: createDesc.trim() }, { headers: authHeader() });
      setRooms(prev => [...prev, r.data]);
      setShowCreateRoom(false);
      setCreateName('');
      setCreateDesc('');
      setSelectedRoom(r.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al crear sala.');
    } finally {
      setCreatingRoom(false);
    }
  };

  // ── Invitar a sala ─────────────────────────────────────────────
  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteMsg('');
    try {
      const r = await axios.post(`${API}/rooms/${selectedRoom.id}/members/`, { username: inviteUsername }, { headers: authHeader() });
      setInviteMsg(r.data.message || 'Invitación enviada.');
      setInviteUsername('');
    } catch (err) {
      setInviteMsg(err.response?.data?.error || 'Error al invitar.');
    }
  };

  // ── Expulsar miembro ───────────────────────────────────────────
  const handleKick = async (memberId, username) => {
    if (!window.confirm(`¿Expulsar a @${username} de la sala?`)) return;
    try {
      await axios.delete(`${API}/rooms/${selectedRoom.id}/members/${memberId}/`, { headers: authHeader() });
      setRoomMembers(prev => prev.filter(m => m.id !== memberId));
    } catch (err) {
      alert(err.response?.data?.error || 'Error al expulsar miembro.');
    }
  };

  // ── Salir de sala ──────────────────────────────────────────────
  const handleLeave = async () => {
    if (!window.confirm('¿Salir de esta sala?')) return;
    const myMembership = roomMembers.find(m => m.username === currentUser?.username);
    if (!myMembership) return;
    try {
      await axios.delete(`${API}/rooms/${selectedRoom.id}/members/${myMembership.id}/`, { headers: authHeader() });
      setRooms(prev => prev.filter(r => r.id !== selectedRoom.id));
      setSelectedRoom(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al salir.');
    }
  };

  // ── Eliminar sala ──────────────────────────────────────────────
  const handleDeleteRoom = async () => {
    if (!window.confirm(`¿Eliminar la sala "${selectedRoom.name}"? Todos los Q's se perderán.`)) return;
    try {
      await axios.delete(`${API}/rooms/${selectedRoom.id}/`, { headers: authHeader() });
      setRooms(prev => prev.filter(r => r.id !== selectedRoom.id));
      setSelectedRoom(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar sala.');
    }
  };

  const remaining = MAX_CHARS - text.length;
  const isCreator = selectedRoom && selectedRoom.is_creator;

  return (
    <div className={`qs-layout ${isLoggedIn && rooms.length > 0 || isLoggedIn ? 'qs-layout--with-sidebar' : ''}`}>

      {/* ── Sidebar de salas ── */}
      {isLoggedIn && (
        <aside className="qs-sidebar">
          <div className="qs-sidebar-section-title">{t('channels')}</div>

          <button
            className={`qs-sidebar-item ${!selectedRoom ? 'qs-sidebar-item--active' : ''}`}
            onClick={() => setSelectedRoom(null)}
          >
            <span className="qs-sidebar-icon">🌐</span>
            <span className="qs-sidebar-name">{t('public_qs')}</span>
          </button>

          {rooms.length > 0 && (
            <div className="qs-sidebar-section-title qs-sidebar-section-title--rooms">{t('my_rooms')}</div>
          )}

          {rooms.map(room => (
            <button
              key={room.id}
              className={`qs-sidebar-item ${selectedRoom?.id === room.id ? 'qs-sidebar-item--active' : ''}`}
              onClick={() => selectRoom(room)}
            >
              <span className="qs-sidebar-icon">🔒</span>
              <span className="qs-sidebar-name">{room.name}</span>
              <span className="qs-sidebar-members">{room.member_count}</span>
            </button>
          ))}

          {showCreateRoom ? (
            <form className="qs-create-room-form" onSubmit={handleCreateRoom}>
              <input
                type="text"
                placeholder={t('room_name_placeholder')}
                value={createName}
                onChange={e => setCreateName(e.target.value)}
                maxLength={100}
                autoFocus
                required
              />
              <input
                type="text"
                placeholder={t('room_desc_placeholder')}
                value={createDesc}
                onChange={e => setCreateDesc(e.target.value)}
                maxLength={300}
              />
              <div className="qs-create-room-actions">
                <button type="submit" className="qs-create-room-btn" disabled={creatingRoom}>
                  {creatingRoom ? t('creating') : t('create')}
                </button>
                <button type="button" className="qs-create-room-cancel" onClick={() => setShowCreateRoom(false)}>
                  {t('cancel')}
                </button>
              </div>
            </form>
          ) : (
            <button className="qs-sidebar-new-room" onClick={() => setShowCreateRoom(true)}>
              {t('new_room')}
            </button>
          )}
        </aside>
      )}

      {/* ── Main feed ── */}
      <div className="qs-main">

        {/* Header */}
        <div className="qs-header">
          <div className="qs-header-top">
            <div>
              <h2>{selectedRoom ? `🔒 ${selectedRoom.name}` : "Q's"}</h2>
              <p className="qs-subtitle">
                {selectedRoom
                  ? (selectedRoom.description || t('members_count', { count: selectedRoom.member_count }))
                  : t('qs_subtitle')}
              </p>
            </div>
            {selectedRoom && isLoggedIn && (
              <div className="qs-room-actions">
                <button
                  className={`qs-room-panel-btn ${showRoomPanel ? 'active' : ''}`}
                  onClick={() => { setShowRoomPanel(v => !v); fetchRoomMembers(selectedRoom); }}
                >
                  {t('members')}
                </button>
                {!isCreator && (
                  <button className="qs-room-leave-btn" onClick={handleLeave}>{t('leave_room')}</button>
                )}
                {isCreator && (
                  <button className="qs-room-delete-btn" onClick={handleDeleteRoom}>{t('delete_room')}</button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Panel de gestión de sala */}
        {showRoomPanel && selectedRoom && (
          <div className="qs-room-panel">
            <div className="qs-room-panel-cols">
              <div>
                <div className="qs-room-panel-subtitle">Miembros ({roomMembers.length})</div>
                <ul className="qs-room-member-list">
                  {roomMembers.map(m => (
                    <li key={m.id} className="qs-room-member-item">
                      <Avatar username={m.username} photoUrl={m.photo_url} size={28} />
                      <span className="qs-room-member-name">@{m.username}</span>
                      {m.username === selectedRoom.creator_username && (
                        <span className="qs-room-creator-badge">{t('creator_badge')}</span>
                      )}
                      {isCreator && m.username !== currentUser?.username && (
                        <button className="qs-room-kick-btn" onClick={() => handleKick(m.id, m.username)}>
                          {t('kick')}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {isCreator && (
                <div>
                  <div className="qs-room-panel-subtitle">{t('invite_user')}</div>
                  <form className="qs-invite-form" onSubmit={handleInvite}>
                    <input
                      type="text"
                      placeholder="Username"
                      value={inviteUsername}
                      onChange={e => setInviteUsername(e.target.value)}
                      required
                    />
                    <button type="submit">{t('invite')}</button>
                  </form>
                  {inviteMsg && (
                    <p className={`qs-invite-msg ${inviteMsg.startsWith('Error') || inviteMsg.startsWith('@') ? 'qs-invite-msg--error' : ''}`}>
                      {inviteMsg}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Composer */}
        {isLoggedIn ? (
          <div className="qs-composer">
            <Avatar username={currentUser?.username} photoUrl={currentUser?.photo_url} size={42} />
            <div className="qs-composer-inner">
              {selectedRoom && (
                <div className="qs-room-badge">{t('posting_in')} 🔒 {selectedRoom.name}</div>
              )}
              <textarea
                className="qs-textarea"
                placeholder={selectedRoom ? `${t('posting_in')} ${selectedRoom.name}...` : t('qs_placeholder')}
                value={text}
                onChange={e => setText(e.target.value.slice(0, MAX_CHARS))}
                rows={3}
              />

              {/* Grabador de voz en vivo */}
              {recording && (
                <div className="qs-recorder">
                  <span className="qs-recorder-dot" />
                  <span className="qs-recorder-time">{fmtTime(recSeconds)}</span>
                  <div className="qs-recorder-bar">
                    <div className="qs-recorder-bar-fill" style={{ width: `${(recSeconds / MAX_REC) * 100}%` }} />
                  </div>
                  <span className="qs-recorder-limit">{fmtTime(MAX_REC - recSeconds)}</span>
                  <button className="qs-recorder-stop" onClick={stopRecording} title={t('stop_recording')}>⏹</button>
                </div>
              )}

              {/* Preview del audio grabado */}
              {audioUrl && !recording && (
                <div className="qs-audio-preview">
                  <div className="qs-audio-preview-top">
                    <span className="qs-audio-preview-icon">🎤</span>
                    <span className="qs-audio-preview-dur">{fmtTime(audioDuration)}</span>
                    <button className="qs-audio-discard" onClick={discardAudio} title={t('discard_audio')}>✕ {t('discard_audio')}</button>
                  </div>
                  <audio controls src={audioUrl} className="qs-audio-preview-player" />
                </div>
              )}

              <div className="qs-composer-footer">
                <div className="qs-composer-tools">
                  {!audioUrl && !recording && (
                    <button
                      className="qs-mic-btn"
                      onClick={startRecording}
                      title={t('record_voice')}
                      type="button"
                    >
                      🎤
                    </button>
                  )}
                  <span className={`qs-char-count ${remaining < 20 ? 'qs-char-count--warn' : ''} ${remaining < 0 ? 'qs-char-count--over' : ''}`}>
                    {remaining}
                  </span>
                </div>
                <button
                  className="qs-post-btn"
                  onClick={handlePost}
                  disabled={posting || (!text.trim() && !audioBlob) || remaining < 0}
                >
                  {posting ? t('posting') : selectedRoom ? t('post_in_room') : t('post_q')}
                </button>
              </div>
              {postError && <p className="qs-post-error">{postError}</p>}
            </div>
          </div>
        ) : (
          <div className="qs-gate">
            <div className="qs-gate-icon">💬</div>
            <h3>{t('join_conversation')}</h3>
            <p>{t('join_conversation_text')}</p>
            <p className="qs-gate-sub">{t('join_conversation_sub')}</p>
          </div>
        )}

        {/* Feed */}
        <div className="qs-feed">
          {loading && <div className="loading">{t('loading_qs')}</div>}
          {error   && <div className="error">{error}</div>}

          {!loading && !error && posts.length === 0 && (
            <div className="qs-empty">
              <p>{selectedRoom ? t('no_qs_room') : t('no_qs_public')}</p>
            </div>
          )}

          {posts.map(post => (
            <div key={post.id} className="qs-post-wrap">
              {/* Header de reQ */}
              {post.original_post && (
                <div className="qs-repost-header">
                  🔁 <span
                    style={{ cursor: onViewProfile ? 'pointer' : 'default' }}
                    onClick={() => onViewProfile && onViewProfile(post.username)}
                  >@{post.username}</span> {t('reposted')}
                </div>
              )}

              <div className="qs-post">
                <div
                  className="qs-post-avatar"
                  style={{ cursor: onViewProfile ? 'pointer' : 'default' }}
                  onClick={() => onViewProfile && onViewProfile(post.original_post ? post.original_post.username : post.username)}
                >
                  <Avatar
                    username={post.original_post ? post.original_post.username : post.username}
                    photoUrl={post.original_post ? null : post.photo_url}
                    size={40}
                  />
                </div>
                <div className="qs-post-body">
                  <div className="qs-post-meta">
                    <span
                      className="qs-post-username"
                      style={{ cursor: onViewProfile ? 'pointer' : 'default' }}
                      onClick={() => onViewProfile && onViewProfile(post.original_post ? post.original_post.username : post.username)}
                    >@{post.original_post ? post.original_post.username : post.username}</span>
                    <span className="qs-post-time">{timeAgo(post.original_post ? post.original_post.created_at : post.created_at)}</span>
                    {isLoggedIn && currentUser?.username === post.username && (
                      <button className="qs-delete-btn" onClick={() => handleDelete(post.id)} title="Eliminar">✕</button>
                    )}
                  </div>

                  {/* Texto de cita (si aplica) */}
                  {post.original_post && post.text && (
                    <p className="qs-post-text">{renderPostText(post.text)}</p>
                  )}

                  {/* Texto del post (normal o post original en reQ simple) */}
                  {post.original_post ? (
                    <div className="qs-quoted-post">
                      {post.original_post.text && <p className="qs-post-text">{renderPostText(post.original_post.text)}</p>}
                    </div>
                  ) : (
                    <>
                      {post.text && <p className="qs-post-text">{renderPostText(post.text)}</p>}
                      {post.audio_url && (
                        <div className="qs-audio-player">
                          <span className="qs-audio-player-icon">🎤</span>
                          <audio controls src={post.audio_url} className="qs-audio-el" preload="none" />
                          {post.audio_duration && (
                            <span className="qs-audio-dur">{fmtTime(post.audio_duration)}</span>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  <div className="qs-post-actions">
                    <div className="qs-post-actions-left">
                      <button
                        className={`qs-like-btn ${post.liked_by_me ? 'qs-like-btn--active' : ''} ${!isLoggedIn ? 'qs-like-btn--disabled' : ''}`}
                        onClick={() => handleLike(post)}
                        disabled={!isLoggedIn}
                      >
                        ♥ <span>{post.likes_count || 0}</span>
                      </button>
                      <button
                        className={`qs-comment-toggle-btn ${expanded.has(post.id) ? 'qs-comment-toggle-btn--active' : ''}`}
                        onClick={() => toggleComments(post.id)}
                      >
                        💬 <span>{post.comments_count || 0}</span>
                      </button>
                      {isLoggedIn && !selectedRoom && currentUser?.username !== (post.original_post ? post.original_post.username : post.username) && !post.original_post && (
                        <div className="qs-repost-wrap">
                          <button
                            className={`qs-repost-btn ${post.reposted_by_me ? 'qs-repost-btn--active' : ''}`}
                            onClick={() => handleRepost(post)}
                            title={post.reposted_by_me ? t('undo_req') : t('req')}
                          >
                            🔁 <span>{post.reposts_count || 0}</span>
                          </button>
                          <button
                            className="qs-quote-btn"
                            onClick={() => { setQuoting(post); setQuoteText(''); }}
                            title={t('quote_req')}
                          >
                            ✏️
                          </button>
                        </div>
                      )}
                    </div>
                    {isLoggedIn && currentUser?.username !== (post.original_post ? post.original_post.username : post.username) && (
                      <div className="qs-moderation-btns">
                        <button
                          className="qs-mute-btn"
                          onClick={() => handleMute(post.original_post ? post.original_post.username : post.username)}
                          title={t('mute_user', { username: post.original_post ? post.original_post.username : post.username })}
                        >🚫</button>
                        <button
                          className="qs-report-btn"
                          onClick={() => { setReporting(post); setReportReason(''); setReportDetail(''); setReportSent(false); }}
                          title={t('report_post')}
                        >🚩</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {expanded.has(post.id) && (
                <CommentsSection post={post} currentUser={currentUser} isLoggedIn={isLoggedIn} />
              )}
            </div>
          ))}

          {/* Modal denunciar Q */}
          {reporting && (
            <div className="qs-report-overlay" onClick={closeReport}>
              <div className="qs-report-modal" onClick={e => e.stopPropagation()}>
                <div className="qs-report-modal-header">
                  <span>{t('report_post')}</span>
                  <button className="qs-report-close" onClick={closeReport}>✕</button>
                </div>
                {reportSent ? (
                  <div className="qs-report-sent">
                    <span>✅</span>
                    <p>{t('report_sent_msg')}</p>
                  </div>
                ) : (
                  <>
                    <div className="qs-report-reasons">
                      {[
                        { value: 'fake_news',  label: t('fake_news') },
                        { value: 'language',   label: t('bad_language') },
                        { value: 'other',      label: t('other_reason') },
                      ].map(opt => (
                        <label key={opt.value} className={`qs-report-reason ${reportReason === opt.value ? 'qs-report-reason--selected' : ''}`}>
                          <input
                            type="radio"
                            name="reportReason"
                            value={opt.value}
                            checked={reportReason === opt.value}
                            onChange={() => setReportReason(opt.value)}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                    {reportReason === 'other' && (
                      <textarea
                        className="qs-report-detail"
                        placeholder={t('report_detail_placeholder')}
                        value={reportDetail}
                        onChange={e => setReportDetail(e.target.value)}
                        maxLength={280}
                      />
                    )}
                    <div className="qs-report-modal-footer">
                      <button
                        className="qs-report-submit"
                        onClick={handleReportSubmit}
                        disabled={!reportReason}
                      >{t('send_report')}</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Modal citar reQ */}
          {quoting && (
            <div className="qs-quote-overlay" onClick={() => setQuoting(null)}>
              <div className="qs-quote-modal" onClick={e => e.stopPropagation()}>
                <div className="qs-quote-modal-header">
                  <span>{t('quote_req')}</span>
                  <button className="qs-quote-close" onClick={() => setQuoting(null)}>✕</button>
                </div>
                <div className="qs-quoted-preview">
                  <span className="qs-quoted-preview-username">@{quoting.username}</span>
                  <p>{quoting.text}</p>
                </div>
                <textarea
                  className="qs-quote-textarea"
                  placeholder={t('add_comment')}
                  value={quoteText}
                  onChange={e => setQuoteText(e.target.value)}
                  maxLength={280}
                  autoFocus
                />
                <div className="qs-quote-modal-footer">
                  <span className="qs-quote-count">{quoteText.length}/280</span>
                  <button
                    className="qs-quote-submit"
                    onClick={handleQuoteSubmit}
                    disabled={!quoteText.trim()}
                  >{t('publish_req')}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QsFeed;
